import { Controller } from "@hotwired/stimulus"
import {
  buildAutocompleteUrl,
  parseAutocompleteResponse,
  type AutocompleteSuggestion,
} from "@/services/geoapify_autocomplete"

const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 3
const SUGGESTION_LIMIT = 5

/**
 * U.S. address suggestions via server-proxied Geoapify autocomplete; pairs with server geocode on submit.
 */
export default class extends Controller {
  static override targets = ["input", "list"]

  declare readonly inputTarget: HTMLInputElement
  declare readonly listTarget: HTMLUListElement

  private debounceTimer: number | undefined
  private fetchController: AbortController | undefined
  private readonly boundDocClick = (e: Event) => {
    this.onDocumentClick(e)
  }
  private suggestions: AutocompleteSuggestion[] = []
  private activeIndex = -1

  override connect(): void {
    if (!this.element.id) {
      this.element.id = `address-ac-${Math.random().toString(36).slice(2, 11)}`
    }
    this.inputTarget.setAttribute("role", "combobox")
    this.inputTarget.setAttribute("aria-haspopup", "listbox")
    this.inputTarget.setAttribute("aria-autocomplete", "list")
    this.inputTarget.setAttribute("aria-expanded", "false")
    if (!this.listTarget.id) {
      this.listTarget.id = `${this.element.id}-listbox`
    }
    this.listTarget.setAttribute("role", "listbox")
    this.inputTarget.setAttribute("aria-controls", this.listTarget.id)
    document.addEventListener("click", this.boundDocClick, true)
  }

  override disconnect(): void {
    document.removeEventListener("click", this.boundDocClick, true)
    this.clearDebounce()
    this.abortFetch()
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  onInput(): void {
    this.clearDebounce()
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = undefined
      void this.runQuery()
    }, DEBOUNCE_MS)
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.isListVisible()) {
      event.preventDefault()
      this.closeList()
      return
    }

    if (event.key === "ArrowDown" && !this.isListVisible()) {
      if (this.inputTarget.value.trim().length < MIN_QUERY_LENGTH) return
      event.preventDefault()
      void (async () => {
        await this.runQuery()
        if (this.suggestions.length > 0) {
          this.activeIndex = 0
          this.updateActiveOption()
        }
      })()
      return
    }

    if (!this.isListVisible()) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1)
      this.updateActiveOption()
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, -1)
      this.updateActiveOption()
      return
    }

    if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault()
      this.selectIndex(this.activeIndex)
    }
  }

  onSuggestionMouseDown(event: Event): void {
    event.preventDefault()
  }

  onSuggestionClick(event: Event): void {
    const li = (event.currentTarget as HTMLElement).closest("li[role='option']")
    if (!li) return
    const idx = Number((li as HTMLElement).dataset.index)
    if (Number.isFinite(idx) && idx >= 0) {
      this.selectIndex(idx)
    }
  }

  private isListVisible(): boolean {
    return !this.listTarget.hidden && this.suggestions.length > 0
  }

  private clearDebounce(): void {
    if (this.debounceTimer !== undefined) {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = undefined
    }
  }

  private abortFetch(): void {
    this.fetchController?.abort()
    this.fetchController = undefined
  }

  private async runQuery(): Promise<void> {
    const query = this.inputTarget.value.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      this.hideList()
      return
    }

    this.abortFetch()
    this.fetchController = new AbortController()
    const { signal } = this.fetchController

    const url = buildAutocompleteUrl(query, { limit: SUGGESTION_LIMIT })

    try {
      const response = await fetch(url, { signal, method: "GET", headers: { Accept: "application/json" } })
      if (!response.ok) {
        this.hideList()
        return
      }
      const json: unknown = await response.json()
      this.suggestions = parseAutocompleteResponse(json)
      this.activeIndex = -1
      if (this.suggestions.length === 0) {
        this.hideList()
        return
      }
      this.renderList()
      this.openList()
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return
      this.hideList()
    } finally {
      this.fetchController = undefined
    }
  }

  private renderList(): void {
    this.listTarget.innerHTML = ""
    for (let i = 0; i < this.suggestions.length; i++) {
      const s = this.suggestions[i]!
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.id = `address-suggestion-${this.element.id}-${i}`
      li.dataset.index = String(i)
      li.textContent = s.formatted
      li.addEventListener("mousedown", (e) => this.onSuggestionMouseDown(e))
      li.addEventListener("click", (e) => this.onSuggestionClick(e))
      this.listTarget.appendChild(li)
    }
  }

  private updateActiveOption(): void {
    const options = this.listTarget.querySelectorAll<HTMLElement>("li[role='option']")
    options.forEach((el, i) => {
      if (i === this.activeIndex) {
        el.classList.add("is-active")
        el.setAttribute("aria-selected", "true")
      } else {
        el.classList.remove("is-active")
        el.removeAttribute("aria-selected")
      }
    })
    if (this.activeIndex >= 0 && options[this.activeIndex]) {
      this.inputTarget.setAttribute("aria-activedescendant", options[this.activeIndex]!.id)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  private openList(): void {
    this.listTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  private selectIndex(i: number): void {
    const s = this.suggestions[i]
    if (!s) return
    this.inputTarget.value = s.formatted
    this.closeList()
    this.inputTarget.focus()
  }

  private hideList(): void {
    this.suggestions = []
    this.listTarget.innerHTML = ""
    this.listTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  private closeList(): void {
    this.hideList()
  }

  private onDocumentClick(event: Event): void {
    if (!(event.target instanceof Node)) return
    if (!this.element.contains(event.target)) {
      this.hideList()
    }
  }
}
